import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["expanded", "iconTheme", "expandAll", "hiddenTextPlaceholder", "item", "itemKey", "onExpandedChange", "cellContent", "customStrings", "liveRegionNodeRef"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button, IconCollapse, IconExpand } from "../../";
import LiveRegion from "../../private/LiveRegion/LiveRegion";
import Tooltip from "../../private/Tooltip/Tooltip";
import { CLEAR_ANNOUNCEMENT_TIMEOUT } from "../constants";
import STRINGS from "../strings";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

const ExpandableItemButton = _ref => {
  let {
    expanded,
    iconTheme,
    expandAll,
    hiddenTextPlaceholder,
    // eslint-disable-line no-unused-vars
    item,
    // eslint-disable-line no-unused-vars
    itemKey,
    // eslint-disable-line no-unused-vars
    onExpandedChange,
    // eslint-disable-line no-unused-vars
    cellContent,
    // eslint-disable-line no-unused-vars, react/prop-types
    customStrings,
    // eslint-disable-line no-unused-vars
    liveRegionNodeRef
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const [liveRegionText, setLiveRegionText] = useState("");

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);
  /**
   * Toggles the expanded state of the item.
   */


  const toggleExpanded = () => {
    onExpandedChange(itemKey, !expanded); // Get reference of the current expanded/collapsed row.

    const {
      collapseAllRowsAnnouncement,
      expandAllRowsAnnouncement,
      collapsedRowAnnouncement,
      expandedRowAnnouncement
    } = componentStrings;
    let textContent;

    if (expanded) {
      textContent = expandAll ? collapseAllRowsAnnouncement(`${hiddenTextPlaceholder}`) : collapsedRowAnnouncement(itemKey, item);
    } else {
      textContent = expandAll ? expandAllRowsAnnouncement(`${hiddenTextPlaceholder}`) : expandedRowAnnouncement(itemKey, item);
    }

    setLiveRegionText(textContent);
  };
  /**
   * Clears the live region announcement text.
   */


  const clearAnnouncement = () => {
    setLiveRegionText("");
  };

  const Icon = expanded ? IconCollapse : IconExpand;
  const label = otherProps["aria-label"];
  return _jsxs(React.Fragment, {
    children: [expandAll ? _jsx(Button, _objectSpread(_objectSpread({
      kind: "simple"
    }, otherProps), {}, {
      onClick: toggleExpanded,
      display: "block",
      fullWidth: true,
      centerIcon: _jsx(Icon, {
        theme: iconTheme,
        size: "xsmall",
        className: "pnr-table__expandable-toggle-icon"
      })
    })) : _jsx(Tooltip, {
      autoPlacement: true,
      label: label,
      trigger: _jsx(Button, _objectSpread(_objectSpread({
        kind: "simple"
      }, otherProps), {}, {
        onClick: toggleExpanded,
        display: "block",
        fullWidth: true,
        centerIcon: _jsx(Icon, {
          theme: iconTheme,
          size: "xsmall",
          className: "pnr-table__expandable-toggle-icon"
        })
      })),
      children: label
    }), _jsx(LiveRegion, {
      politeness: "polite",
      clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
      onAnnouncementClear: clearAnnouncement,
      children: liveRegionText
    })]
  });
};

ExpandableItemButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Item's expanded state. */
  expanded: PropTypes.bool.isRequired,

  /**
   * @param {String|Number} itemKey - `props.itemKey` supplied to this Button.
   * @param {Boolean} expanded - `false` if this Button is currently expanded,
   *                             otherwise `true` (i.e. `!props.expanded`).
   */
  onExpandedChange: PropTypes.func.isRequired,

  /**
   * classname used for displaying tooltip
   */
  className: PropTypes.string,

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    // Used for text 'Expand' on single row button in `Expandable` HOC.
    expandSingle: PropTypes.string,
    // Used for text 'Collapse' on single row button in `Expandable` HOC.
    collapseSingle: PropTypes.string,
    // Used for text 'Expand All' on header button in `Expandable` HOC.
    expandAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    // Used for text 'Collapse All' on header button in `Expandable` HOC.
    collapseAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    // Used for announcing number of rows expanded.
    expandAllRowsAnnouncement: PropTypes.func,
    // Used for announcing number of rows collapsed.
    collapseAllRowsAnnouncement: PropTypes.func,

    /**
     * Used for `more about ${itemKey}` text.
     */
    rowExpandButtonLabel: PropTypes.func,

    /**
     * Used for announcing collapsed row state.
     * Default: `itemKey => more about ${itemKey}, collapsed`.
     */
    collapsedRowAnnouncement: PropTypes.func,

    /**
     * Used for announcing expanded row state.
     * Default: `itemKey => more about ${itemKey}, expanded`.
     */
    expandedRowAnnouncement: PropTypes.func
  }),

  /**
   * Boolean to know if all rows are expanded or not
   */
  expandAll: PropTypes.bool,

  /**
   * Placeholder for number of rows count,
   * used to display in hidden span element for a11y purpose.
   */
  hiddenTextPlaceholder: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * theme used for the expand/collapse icon
   */
  iconTheme: PropTypes.string,

  /**
   * The Row item data object.
   */
  item: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * The unique key for the item
   */
  itemKey: PropTypes.node,

  /**
   * @ignore Use as the [ref](https://reactjs.org/docs/refs-and-the-dom.html).
   * React reference for the HTML `span[aria-live='polite']` node which is
   * actually rendered for `Expand All` button, but using same node for
   * announcing `aria-live` updates of both `Expand All` and `Expand Row`
   * by using this prop.
   */
  liveRegionNodeRef: PropTypes.object // eslint-disable-line react/forbid-prop-types

} : {};
ExpandableItemButton.defaultProps = {
  itemKey: null,
  item: null,
  "aria-label": "Expand",
  iconTheme: "default",
  hiddenTextPlaceholder: "",
  className: "",
  customStrings: {},
  expandAll: false,
  liveRegionNodeRef: /*#__PURE__*/React.createRef()
};
export default ExpandableItemButton;